import { z } from "zod";
import type { PaginationMeta, PaginatedData } from "@core/types/TApiResponse";
import { ApprovalStatusSchema } from "@modules/docs/approvals/types/TApproval";

// Re-export so existing consumers of TApprovalStatus from this path keep working
export type { TApprovalStatus } from "@modules/docs/approvals/types/TApproval";

// ─── Schemas ──────────────────────────────────────────────────────────────────

const ItemPermissionsSchema = z.object({
  can_share: z.boolean(),
  can_create_approval: z.boolean(),
  can_contribute: z.boolean(),
  can_create_version: z.boolean(),
  can_annotate: z.boolean(),
  // Delete action on the item. Optional: when the server omits it (or sends
  // null) the consumer falls back to can_contribute.
  can_delete: z.boolean().nullable().optional(),
});

const DocOwnerSchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  picture: z.string().nullable(),
});

export const DocItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  format: z.string(),
  document_status: z.enum(["active", "locked"]),
  size: z.number().nullable(),
  description: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  owner: DocOwnerSchema,
  is_favorite: z.boolean(),
  current_version: z.string().nullable(),
  approval_status: ApprovalStatusSchema.nullable(),
  approval_id: z.string().nullable(),
  is_owner: z.boolean().nullable(),
  licence: z.enum(["active", "inactive"]).nullable(),
  item_permissions: ItemPermissionsSchema.nullable(),
});

// ─── Domain types (inferred) ──────────────────────────────────────────────────

export type TDocItem = z.infer<typeof DocItemSchema>;
export type TDocOwner = z.infer<typeof DocOwnerSchema>;
export type TItemPermissions = z.infer<typeof ItemPermissionsSchema>;

export type TFolderPermissions = {
  can_upload: boolean;
  can_create_folder: boolean;
};

// ─── String union types ───────────────────────────────────────────────────────

export type TDocFormat =
  | "folder"
  | "pdf"
  | "docx"
  | "xlsx"
  | "pptx"
  | "png"
  | "jpg"
  | "jpeg"
  | "mp4"
  | "zip"
  | string;

export type TDocStatus = "active" | "locked";

// ─── Pagination / response types ──────────────────────────────────────────────

// Extends shared PaginationMeta — adds is_owner only
export type TDocMeta = PaginationMeta & {
  is_owner?: boolean;
};

// Uses PaginatedData but narrows meta to TDocMeta
export type TDocItemsResponse = Omit<PaginatedData<TDocItem>, "meta"> & {
  meta: TDocMeta;
  permissions: TFolderPermissions;
};

// ─── Misc domain types ────────────────────────────────────────────────────────

export type TBreadcrumb = {
  id: string;
  name: string;
};

export type TDocumentTime =
  | "all-time"
  | "last-week"
  | "last-month"
  | "last-3-months";

export type TDocumentType =
  | "xlsx"
  | "pdf"
  | "docx"
  | "txt"
  | "folder"
  | "svg"
  | "png"
  | "jpg"
  | "jpeg"
  | "ifc"
  | "mp4"
  | "pptx"
  | "file"
  | "dwg"
  | "dxf"
  | null;

// ─── Payload types ────────────────────────────────────────────────────────────

export type TDocItemsParams = {
  folder_id?: string | null;
  type?: TDocumentType;
  search?: string;
  recent?: boolean;
  is_fav?: boolean;
  time?: TDocumentTime | null;
  skip?: number;
  page?: number;
  size?: number;
  sort_by?: string;
  sort_type?: "asc" | "desc";
};
